import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import './slider.js';

declare global {
  interface Window {
    audioService: any;
  }
}

@customElement('effect-controls')
export class EffectControls extends LitElement {
  @state() private lowpassFreq = 12000;
  @state() private highpassFreq = 400;
  @state() private delayTime = 1;
  @state() private delayFeedback = 0.4;
  @state() private delayMix = 0.3;
  @state() private delayDampener = 8000;
  @state() private reverbMix = 0.3;

  protected createRenderRoot() {
    return this; // Use light DOM instead of shadow DOM
  }

  protected firstUpdated(): void {
    if (window.audioService) {
      window.audioService.addEventListener(this.handleAudioEvent.bind(this));
    }
  }

  private handleAudioEvent(): void {
    // Handle audio service events if needed
  }

  private updateLowpass(value: number): void {
    this.lowpassFreq = value;
    if (window.audioService) {
      window.audioService.setLowpassFrequency(value);
    }
  }

  private updateHighpass(value: number): void {
    this.highpassFreq = value;
    if (window.audioService) {
      window.audioService.setHighpassFrequency(value);
    }
  }

  private updateDelayTime(value: number): void {
    this.delayTime = value;
    if (window.audioService) {
      window.audioService.setDelayTime(value);
    }
  }

  private updateDelayFeedback(value: number): void {
    this.delayFeedback = value;
    if (window.audioService) {
      window.audioService.setDelayFeedback(value);
    }
  }

  private updateDelayMix(value: number): void {
    this.delayMix = value;
    if (window.audioService) {
      window.audioService.setDelayMix(value);
    }
  }

  private updateDelayDampener(value: number): void {
    this.delayDampener = value;
    if (window.audioService) {
      window.audioService.setDelayDampener(value);
    }
  }

  private updateReverbMix(value: number): void {
    this.reverbMix = value;
    if (window.audioService) {
      window.audioService.setReverbMix(value);
    }
  }


  render() {
    return html`
      <h1>FX <span class="small">recording only</span></h1>
      
      <div class="effects-grid">
        <div class="effect-section">
          <h2>Filters</h2>
          
          <div class="control-group">
            <div class="slider-container">
              <div class="slider-text-row justify-between">
                <span class="text-left">Lowpass</span>
                <span class="value-display">${this.lowpassFreq} Hz</span>
              </div>
              <div class="slider-row">
                <div class="slider-track">
                  <app-slider
                      .value=${this.lowpassFreq}
                      min="100"
                      max="20000"
                      step="100"
                      height="20"
                      foregroundColor="white"
                      backgroundColor="rgba(255, 255, 255, 0.1)"
                      @input=${(e: CustomEvent) => this.updateLowpass(e.detail.value)}
                  ></app-slider>
                </div>
              </div>
            </div>
          </div>
          
          <div class="control-group">
            <div class="slider-container">
              <div class="slider-text-row justify-between">
                <span class="text-left">Highpass</span>
                <span class="value-display">${this.highpassFreq} Hz</span>
              </div>
              <div class="slider-row">
                <div class="slider-track">
                  <app-slider
                    .value=${this.highpassFreq}
                    min="20"
                    max="20000"
                    step="10"
                    height="20"
                    foregroundColor="white"
                    backgroundColor="rgba(255, 255, 255, 0.1)"
                    @input=${(e: CustomEvent) => this.updateHighpass(e.detail.value)}
                  ></app-slider>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="effect-section">
          <h2>Delay</h2>
          
          <div class="control-group">
            <div class="slider-container">
              <div class="slider-text-row justify-between">
                <span class="text-left">Delay Time</span>
                <span class="value-display">${(this.delayTime * 1000).toFixed(0)} ms</span>
              </div>
              <div class="slider-row">
                <div class="slider-track">
                  <app-slider
                    .value=${this.delayTime}
                    min="0.5"
                    max="5"
                    step="0.01"
                    height="20"
                    foregroundColor="white"
                    backgroundColor="rgba(255, 255, 255, 0.1)"
                    @input=${(e: CustomEvent) => this.updateDelayTime(e.detail.value)}
                  ></app-slider>
                </div>
              </div>
            </div>
          </div>
          
          <div class="control-group">
            <div class="slider-container">
              <div class="slider-text-row justify-between">
                <span class="text-left">Feedback</span>
                <span class="value-display">${(this.delayFeedback * 100).toFixed(0)}%</span>
              </div>
              <div class="slider-row">
                <div class="slider-track">
                  <app-slider
                    .value=${this.delayFeedback}
                    min="0"
                    max="0.95"
                    step="0.01"
                    height="20"
                    foregroundColor="white"
                    backgroundColor="rgba(255, 255, 255, 0.1)"
                    @input=${(e: CustomEvent) => this.updateDelayFeedback(e.detail.value)}
                  ></app-slider>
                </div>
              </div>
            </div>
          </div>
          
          <div class="control-group">
            <div class="slider-container">
              <div class="slider-text-row justify-between">
                <span class="text-left">Dampener Frequency</span>
                <span class="value-display">${this.delayDampener} Hz</span>
              </div>
              <div class="slider-row">
                <div class="slider-track">
                  <app-slider
                    .value=${this.delayDampener}
                    min="200"
                    max="20000"
                    step="100"
                    height="20"
                    foregroundColor="white"
                    backgroundColor="rgba(255, 255, 255, 0.1)"
                    @input=${(e: CustomEvent) => this.updateDelayDampener(e.detail.value)}
                  ></app-slider>
                </div>
              </div>
            </div>
          </div>

          <div class="control-group">
            <div class="slider-container">
              <div class="slider-text-row justify-between">
                <span class="text-left">Dry/Wet</span>
                <span class="value-display">${(this.delayMix * 100).toFixed(0)}%</span>
              </div>
              <div class="slider-row">
                <div class="slider-track">
                  <app-slider
                    .value=${this.delayMix}
                    min="0"
                    max="1"
                    step="0.01"
                    height="20"
                    foregroundColor="white"
                    backgroundColor="rgba(255, 255, 255, 0.1)"
                    @input=${(e: CustomEvent) => this.updateDelayMix(e.detail.value)}
                  ></app-slider>
                </div>
              </div>
            </div>
          </div>
          
        </div>

        
        <div class="effect-section">
          <h2>Reverb</h2>
          
          <div class="control-group">
            <div class="slider-container">
              <div class="slider-text-row justify-between">
                <span class="text-left">Dry/Wet</span>
                <span class="value-display">${(this.reverbMix * 100).toFixed(0)}%</span>
              </div>
              <div class="slider-row">
                <div class="slider-track">
                  <app-slider
                    .value=${this.reverbMix}
                    min="0"
                    max="1"
                    step="0.01"
                    height="20"
                    foregroundColor="white"
                    backgroundColor="rgba(255, 255, 255, 0.1)"
                    @input=${(e: CustomEvent) => this.updateReverbMix(e.detail.value)}
                  ></app-slider>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}